import get from 'lodash/get';
import QueryString from 'query-string';

import Browser from '@wetransfer/concorde-browser';

import { AdUnitsService } from '../ad-units-service';
import { optimizePath } from '../../helpers/optimize-path';
import { Vast } from '../../helpers/vast';

import ErrorHandler from '@module/error-handler';
import request from '@module/http/axios/request';

export const AdsProvider = {
  get name() {
    return 'ADS_PROVIDER';
  },

  isRotationEnabled() {
    return true;
  },

  rotationInterval(config, { wallpaper }) {
    const nextPosition = wallpaper.position + 1;

    // The rotation interval can be different based on the wallpaper position.
    // The higher the position, the longer the rotation interval.
    const interval = AdUnitsService.calculateInterval(nextPosition);

    return interval;
  },

  async nextCreative(config, { session, wallpaper }) {
    const nextPosition = wallpaper.position + 1;
    const slots = AdUnitsService.calculateSlots(nextPosition);
    const params = this.buildAdParams(config, slots, session);
    const response = await this.sendRequest(config, params);

    return await this.parseResponse(response, config, slots, nextPosition);
  },

  buildAdParams(config, slots, session) {
    const clientParams = QueryString.parse(location.search);

    const optionalParams = {};

    // Overwrite client IP for testing purposes
    if (clientParams.adzerk_origin_ip) {
      optionalParams.ip = clientParams.adzerk_origin_ip;
    }

    const params = this.buildParams(config, slots, session);
    return {
      ...optionalParams,
      consent: {
        gdpr: true,
      },
      placements: [
        {
          ...params.placement,
          divName: config.adzerk.placements.iframe,
          adTypes: [config.adzerk.adTypes.wallpaper],
        },
      ],
      keywords: params.keywords,
      user: {
        key: `sp-${session.domainUserId}`,
      },
    };
  },

  buildVastParams(config, slots, vastId) {
    const params = this.buildParams(config, slots);

    return {
      consent: {
        gdpr: true,
      },
      placements: [
        {
          ...params.placement,
          divName: config.adzerk.placements.video,
          adTypes: [config.adzerk.adTypes.video],
          adId: vastId,
        },
      ],
      keywords: params.keywords,
    };
  },

  buildParams(config, slots, session = {}) {
    // Force display of a specific ad for testing purposes using the ad id and the keyword `test`.
    const clientParams = QueryString.parse(location.search);
    // User locale is the first accepted locale on HTTP_ACCEPT_LANGUAGE header
    // or the application language as fallback
    const userLocale = get(
      config,
      'session.user.acceptedLanguages[0]',
      session.language
    );

    // Create events for default VAST tracking (IDs 70 to 79)
    const vastIDs = Array(10)
      .fill(0)
      .map((value, index) => index + 70);

    // Create 200 custom events, from 800 to 1000
    // https://dev.adzerk.com/docs/custom-event-tracking#section-eventid-mapping-unnamed-events
    const customEventsIDs = Array(201)
      .fill(0)
      .map((value, index) => index + 800);

    const params = {
      placement: {
        networkId: config.adzerk.networkId,
        siteId: config.adzerk.siteId,
        zoneIds: slots,
        eventIds: vastIDs.concat(customEventsIDs),
      },
      keywords: [`user_locale:${userLocale}`],
    };

    const adIdParam = parseInt(clientParams.ad_id, 10);
    if (isFinite(adIdParam)) {
      params.placement.adId = adIdParam;
      params.keywords.push('test');
    }

    // Don't get confused by the name, isTablet actually checks for iPads...
    if (Browser.isTablet) {
      params.keywords.push('ipad');
    }

    return params;
  },

  sendRequest(config, params) {
    return request({ path: `${config.adzerk.api}`, method: 'post', params });
  },

  async parseResponse(response, config, slots, position) {
    const placement = config.adzerk.placements.iframe;
    const ad = get(response, `body.decisions.${placement}`, null);
    if (ad === null) throw new Error('NoAdverts');

    // Add some metadata about creatives, to make it easier to debug
    ErrorHandler.addMetadata('creative_id', ad.adId);
    ErrorHandler.addMetadata('flight_id', ad.flightId);

    const creative = get(ad, 'contents[0].data', null);
    if (creative === null) throw new Error('NoCreative');

    const src = optimizePath({ src: `${config.host}${creative.ctDaveURL}` });

    ErrorHandler.addMetadata('creative_src', src);
    const customEvents = this.processCustomEvents(ad.events);

    const vastTrackers = await this.processVastTrackers(
      creative,
      config,
      slots,
      position
    );

    return {
      id: ad.adId,
      flightId: ad.flightId,
      src: src,
      format: creative.ctFormat,
      clickUrl: ad.clickUrl,
      impressionTrackers: [
        ad.impressionUrl,
        creative.ct3rdPartyTrack,
        creative.ct3rdPartyTrack2,
        creative.ct3rdPartyTrack3,
        creative.ct3rdPartyTrack4,
        creative.ct3rdPartyTrack5,
      ].filter(Boolean),
      vastTrackers,
      customEvents,
      scripts: [creative.ctMOATTrack].filter(Boolean),
      meta: {
        href: ad.clickUrl,
        title: get(creative, 'ctCreativeTitle', ''),
      },
    };
  },

  processCustomEvents(events = []) {
    return events.reduce((customEvents, event) => {
      customEvents[event.id] = event.url;
      return customEvents;
    }, {});
  },

  processVastTrackers(creative, config, slots) {
    // VAST creative IDs are defined in the main creative on the adserver
    const ctVastIds = get(creative, 'ctVastIds', '');
    if (ctVastIds.length === 0) return null;

    // ctVastIds is a string of ids separated with ","
    // Convert them to an array of numbers
    const vastIds = ctVastIds
      .split(',')
      .map((vastId) => parseInt(vastId, 10))
      .filter(Boolean);

    // For each video defined in the creative, we need to request its tracking events
    const vastResponses = vastIds.map(async (vastId) => {
      const params = this.buildVastParams(config, slots, vastId);
      const response = await this.sendRequest(config, params);

      const placement = config.adzerk.placements.video;
      const vast = get(response, `body.decisions.${placement}.contents[0]`);
      const videoUrl = get(vast, 'data.ctadTagURI');
      const body = get(vast, 'body');

      if (!vast) throw new Error(`NoVastTracker for VAST id ${vastId}`);
      if (!videoUrl) throw new Error(`NoVastURI for VAST id ${vastId}`);
      if (!body) throw new Error(`NoVastBody for VAST id ${vastId}`);

      return {
        videoUrl,
        body,
      };
    });

    // Transform the list of VAST responses into and object that contains
    // the name of video and the trackers related to that video
    return Promise.all(vastResponses).then((vastCreatives) => {
      return vastCreatives.reduce((vastTrackers, creative) => {
        vastTrackers[creative.videoUrl] = Vast.parseBody(creative.body);
        return vastTrackers;
      }, {});
    });
  },
};
